import { useState, type FormEvent } from 'react';
import { X, UserPlus, CheckCircle2, AlertTriangle, Loader2, Info } from 'lucide-react';
import { sites as sitesApi, adminUsers, ApiError } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// No "Assign to studies" here any more (Harish, 13 Oct 2026): an invitee
// lands as Site User, the least-privileged role, so a study role given at
// invite could not be used. Flow: invite → Site Team › Change role → then
// Study › Manage staff (or Monitor Assignments for monitors/auditors).

// DREAM-9 + Sprint-1 follow-up: Platform Admin callers pick the role from
// a dropdown ({Site Admin, Platform Admin}); Site Admin callers always
// create Monitor (no dropdown). Backend enforces the same hierarchy
// server-side, so the dropdown is purely UI convenience.
const PLATFORM_INVITER_ROLES = new Set(['platform_admin', 'application_admin']);

type InviteRole = 'site_user' | 'site_admin' | 'platform_admin';

interface Props {
  onClose: () => void;
}

export function InviteModal({ onClose }: Props) {
  const { user } = useAuth();
  const isPlatformInviter = !!user && PLATFORM_INVITER_ROLES.has(user.role);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<InviteRole>('site_user');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Work email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      if (isPlatformInviter && role === 'platform_admin') {
        // Platform Admin path — global role, not tied to a site. Uses the
        // bootstrap create endpoint which mints a reset token + sends the
        // setup email.
        await adminUsers.create({
          email: email.trim(),
          full_name: fullName.trim() || undefined,
          role: 'platform_admin',
          send_invite: true,
        });
      } else {
        // Site-scoped invite. Platform inviters may elevate to site_admin;
        // everyone else (and the default) lands as site_user.
        await sitesApi.invite(user!.activeSiteId, {
          email: email.trim(),
          full_name: fullName.trim() || undefined,
          role: isPlatformInviter ? role : undefined,
        });
      }
      setSuccess(true);
      toast.success(`Invitation sent to ${email.trim()}`);
    } catch (err) {
      let msg = 'Could not send invitation. Please try again.';
      if (err instanceof ApiError) {
        if (err.code === 'CONFLICT') msg = 'This person is already a member of this site.';
        else if (err.code === 'RATE_LIMITED') msg = 'Too many requests. Please wait and try again.';
        else if (err.code === 'VALIDATION_ERROR') msg = 'Please check your entries and try again.';
        else msg = err.message || msg;
      }
      // Keep inline copy so the modal still surfaces the reason; toast also fires.
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const siteName = user?.siteNames?.[user.activeSiteId] ?? 'this site';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-accent-50 grid place-items-center shrink-0">
              <UserPlus className="h-4 w-4 text-accent-700" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-ink-primary leading-tight">
                Invite Team Member
              </h2>
              <p className="text-[11px] text-ink-muted leading-tight mt-0.5">
                {siteName} · Setup link valid for 48 hours
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-lg text-ink-muted hover:bg-surface-subtle hover:text-ink-primary transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {success ? (
            <div className="text-center py-3">
              <div className="h-12 w-12 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <p className="text-[14px] font-semibold text-ink-primary mb-1">Invitation sent</p>
              <p className="text-[12px] text-ink-secondary leading-relaxed">
                A setup link was sent to <strong className="text-ink-primary">{email}</strong>. It
                expires in 48 hours.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 w-full h-10 rounded-lg bg-accent-600 text-white text-[13px] font-semibold hover:bg-accent-700 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-1.5">
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="colleague@organisation.com"
                    maxLength={60}
                    className="w-full h-10 rounded-lg border border-surface-border bg-surface-subtle px-3 text-[13px] text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100 transition-colors"
                  />
                </div>

                {/* Role — Platform Admin chooses; site_admin path is fixed. */}
                {isPlatformInviter ? (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-1.5">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value as InviteRole)}
                      className="w-full h-10 rounded-lg border border-surface-border bg-surface-subtle px-3 text-[13px] text-ink-primary focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100 transition-colors"
                    >
                      <option value="site_user">
                        Site User — lowest privilege; assign a study role later
                      </option>
                      <option value="site_admin">
                        Site Admin — manages {siteName} and invites its team
                      </option>
                      <option value="platform_admin">
                        Platform Admin — cross-tenant root, every site
                      </option>
                    </select>
                    <p className="mt-1.5 text-[10px] text-ink-muted leading-snug">
                      {role === 'platform_admin'
                        ? 'Receives a password-reset link. Platform Admins manage every site, every study, and every user.'
                        : role === 'site_admin'
                          ? `Receives a setup link valid for 48 hours. Manages ${siteName} and invites team members.`
                          : `Receives a setup link valid for 48 hours. Sees only the visit schedule and site-visible documents until you assign a per-study role.`}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 text-[11px] text-blue-900">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      Invitee will be created as <strong>Site User</strong>. They&apos;ll see only
                      the visit schedule and any documents you have marked
                      &ldquo;Visible to Site Users&rdquo;. When you&apos;re ready, change their
                      role on <strong>Site Team</strong>, then add them to a study from the Study
                      Workspace (or Monitor Assignments for monitors and auditors).
                    </span>
                  </div>
                )}

                {/* Full Name (optional) */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-1.5">
                    Full Name{' '}
                    <span className="font-normal normal-case tracking-normal text-ink-muted">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Smith"
                    maxLength={60}
                    className="w-full h-10 rounded-lg border border-surface-border bg-surface-subtle px-3 text-[13px] text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100 transition-colors"
                  />
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-[11px] text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-10 rounded-lg border border-surface-border text-[13px] font-medium text-ink-secondary hover:bg-surface-subtle transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 h-10 rounded-lg bg-accent-600 text-white text-[13px] font-semibold hover:bg-accent-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-3.5 w-3.5" /> Send Invite
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
